import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import AnimatedNumber from '../../utils/AnimatedNumber';
import CourtPanel from './CourtPanel';
import PartnershipSplitNotice from './PartnershipSplitNotice';
import TeamPanel from './TeamPanel';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import CourtTimer from './CourtTimer';
import MatchmakingRelaxedBadge from './MatchmakingRelaxedBadge';
import ScheduledGameLockBadge from './ScheduledGameLockBadge';
import PegBoardPickerHint, {
  PegBoardFillFirstHint,
  PegBoardWaitingForGame,
  PegBoardYourTurnHint,
  pegBoardPickerFromQueue,
} from '../pegBoard/PegBoardPickerHint';
import {
  formatPegBoardPickTargetLabel,
  isPegBoardPickTargetActive,
} from '../../utils/pegBoard';
import { getSinglesClubEloRating } from '../../utils/playerUtils';
import { courtRosterSig } from '../../utils/courtUtils';
import { isAssignmentRowLockedByScheduledGame } from '../../utils/scheduledGameConsumption';
import { handleMobileScoreFocus, markSkipMobileScoreClear, mobileScoreInputAttrs } from '../../utils/mobileScoreInput';
import { scoreSubmitDebugLog } from '../../utils/scoreSubmitDebug';
import {
  DEFAULT_SCORING,
  getScoringContextLabel,
  validateGameScore,
} from '../../utils/gameScoreRules';

// Debug tooltip component that shows top 3 matchmaking combinations
function MatchmakingDebugTooltip({ debugInfo, playersById }) {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const triggerRef = useRef(null);

  useEffect(() => {
    if (isHovered && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 400;
      const tooltipHeight = 300;
      
      let top = rect.bottom + 8; // Position below
      let left = rect.right - tooltipWidth; // Align to right edge
      
      // Ensure tooltip doesn't go off screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      if (left < 8) {
        left = 8;
      }
      if (left + tooltipWidth > viewportWidth) {
        left = viewportWidth - tooltipWidth - 8;
      }
      
      if (top + tooltipHeight > viewportHeight) {
        top = rect.top - tooltipHeight - 8; // Position above if not enough space below
      }
      if (top < 8) {
        top = 8;
      }
      
      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 99999
      });
    }
  }, [isHovered]);

  if (!debugInfo || (
    (!debugInfo.topCombinations || debugInfo.topCombinations.length === 0)
    && !debugInfo.varietyMetrics
  )) {
    return null;
  }

  const varietyMetrics = debugInfo.varietyMetrics;
  const hasCombinations = debugInfo.topCombinations && debugInfo.topCombinations.length > 0;

  return (
    <>
      <span 
        ref={triggerRef}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-info-light text-info text-xs font-semibold cursor-help hover:bg-info/20 transition-colors"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Matchmaking debug info"
      >
        ?
      </span>
      {isHovered && createPortal(
        <div 
          className="px-4 py-3 bg-surface border-2 border-border-medium rounded-lg shadow-xl max-w-md pointer-events-none"
          style={tooltipStyle}
        >
          <div className="font-bold mb-2 text-sm text-text-primary border-b border-border-medium pb-1">
            {hasCombinations ? 'Top 3 Matchmaking Combinations' : 'Matchmaking Variety Metrics'}
          </div>
          {varietyMetrics && (
            <div className="mb-3 p-2 rounded border border-border-medium bg-surface-subtle text-xs space-y-1">
              <div className="font-semibold text-text-primary">Session saturation</div>
              <div className="text-text-secondary">
                Score: <span className="font-medium">{Number(varietyMetrics.sessionSaturationScore ?? 0).toFixed(3)}</span>
                {' · '}
                Pair sat: {Number(varietyMetrics.pairSaturation ?? 0).toFixed(3)}
                {' · '}
                Repeat quartets: {Number(varietyMetrics.repeatQuartetRate ?? 0).toFixed(3)}
              </div>
              {varietyMetrics.shapingReason && (
                <div className="text-text-muted">Shaping: {varietyMetrics.shapingReason}</div>
              )}
              {varietyMetrics.shapingApplied && (
                <div className="text-info">Variety shaping applied</div>
              )}
            </div>
          )}
          {hasCombinations && (
          <>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {debugInfo.topCombinations.map((combo, idx) => {
              const isSelected = idx === 0;
              return (
                <div 
                  key={idx}
                  className={`p-2 rounded border ${isSelected ? 'bg-success-light border-success' : 'bg-surface-subtle border-border-medium'}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${isSelected ? 'text-success-dark' : 'text-text-secondary'}`}>
                      {isSelected ? '✓ Selected' : `#${idx + 1}`}
                    </span>
                    <span className={`text-xs font-bold ${isSelected ? 'text-success-dark' : 'text-text-secondary'}`}>
                      Score: {combo.score.toFixed(0)}
                    </span>
                  </div>
                  <div className="text-xs space-y-0.5">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-team1">Team 1:</span>
                      <span className="text-text-secondary">{combo.team1Names.join(' + ')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-team2">Team 2:</span>
                      <span className="text-text-secondary">{combo.team2Names.join(' + ')}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {debugInfo.selectedScore !== null && (
            <div className="mt-2 pt-2 border-t border-border-medium text-xs text-text-secondary">
              Selected score: <span className="font-semibold text-success-dark">{debugInfo.selectedScore.toFixed(0)}</span>
            </div>
          )}
          </>
          )}
          {debugInfo.relaxationLevel !== undefined && debugInfo.relaxationLevel > 0 && (
            <div className="mt-2 pt-2 border-t border-warning/30 bg-warning-light rounded p-2">
              <div className="text-xs font-semibold text-warning-dark mb-1">
                ⚡ Relaxation Applied
              </div>
              <div className="text-xs text-warning-dark">
                <span className="font-medium">Level {debugInfo.relaxationLevel}:</span>{' '}
                {debugInfo.relaxationName || 'Constraints relaxed'}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

function DroppableTeamPanel({ id, children, className, isFull }) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled: isFull });
  return (
    <div
      ref={setNodeRef}
      className={`${className} min-h-[60px] flex flex-wrap items-center gap-2 transition-shadow relative
        ${isOver && !isFull ? 'ring-2 ring-green-400' : ''}
        ${isFull ? 'opacity-60' : ''}`}
    >
      {children}
      {isFull && (
        <span className="absolute top-1 right-2 text-xs text-red-500 font-semibold">Full</span>
      )}
    </div>
  );
}

const COURT_NUMBER_MOVE_BTN =
  'flex h-11 w-11 items-center justify-center text-text-secondary transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

function CourtNumberMoveChevron({ direction }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      {direction === -1 ? (
        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
      ) : (
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
      )}
    </svg>
  );
}

const CourtAssignment = ({ court, courtIndex, onStartMatch, onUpdateResult, onRotatePlayers, onRemoveCourt, onRemovePlayerFromCourt, onFillEmptyCourt, onPromoteUpcomingMatches, onOpenPegBoardPick, nextAssignments = [], currentClubId = 'default', playersById, isSocialSession = false, availablePlayers = [], waitingManagementPlayers = [], sessionPlayerStatus = {}, onAddPlayerToCourt, matchmakingValidation = { possible: true, reason: '' }, playerStreaks = {}, canEnterMatchScores = true, showPostMatchRotateButton = true, playersPerSide = 2, gridDensity = 'comfortable', highlightFreshRotation = false, playerWaitMetrics = {}, onMoveCourt, courtReorderBusy = false, playingCourtCount = 0, courtMoveFollowsStack = false, courtCardLayout = 'full', viewerPlayerId = null, partnerships = [], scoringConfig = DEFAULT_SCORING, scheduledGames = [], showEloTooltip = true, pegBoardQueue = null, pegBoardPickTarget = null }) => {
  const densityCompact = gridDensity === 'compact';
  /** Roster is tied to a scheduled game — only cancelling it (Manage → Scheduled games) can change the lineup. */
  const rosterLocked = isAssignmentRowLockedByScheduledGame(court, scheduledGames);
  // Debug logging for court data
  // console.log(`🏸 Court ${court.courtNumber} data:`, court);
  // console.log('🔍 CourtAssignment - onRemovePlayerFromCourt:', onRemovePlayerFromCourt, 'isSocialSession:', isSocialSession);
  
  const [isRotating, setIsRotating] = useState(false);
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const [team1Score, setTeam1Score] = useState('');
  const [team2Score, setTeam2Score] = useState('');
  const [testModeEnabled, setTestModeEnabled] = useState(false);
  const [scoreError, setScoreError] = useState(''); // validation errors for entered scores
  const [submitError, setSubmitError] = useState(''); // server/network errors on submit
  
  // Refs for score input fields to enable auto-focus
  const team1ScoreInputRef = useRef(null);
  const team2ScoreInputRef = useRef(null);

  // Clear in-progress score drafts when the court lineup changes (e.g. another host submitted).
  const rosterKey = `${court.courtNumber}:${courtRosterSig(court)}`;
  const prevRosterKeyRef = useRef(rosterKey);
  useEffect(() => {
    if (prevRosterKeyRef.current === rosterKey) return;
    prevRosterKeyRef.current = rosterKey;
    setTeam1Score('');
    setTeam2Score('');
    setScoreError('');
    setSubmitError('');
  }, [rosterKey]);
  
  // Wrapper function to add players to specific positions
  const handleAddPlayerToPosition = (playerId, courtNumber, teamNumber) => {
    if (onAddPlayerToCourt) {
      onAddPlayerToCourt(playerId, courtNumber, teamNumber);
    }
  };
    
  // Badminton scoring validation (shared rules module)
  const validateBadmintonScore = (score1, score2) =>
    validateGameScore(score1, score2, scoringConfig) || '';
  
  // Calculate team average ELO for the current club
  // Defensive: Always use playersById lookups for team1/team2
  const getTeamAvgElo = (teamIds) => {
    const validIds = teamIds.filter(id => playersById[id]);
    if (validIds.length === 0) return 0;
    // Competitive singles: one player per side — team header uses singles Elo
    if (playersPerSide === 1 && validIds.length === 1) {
      return getSinglesClubEloRating(playersById[validIds[0]]);
    }
    if (validIds.length === 2) {
      const [p1Id, p2Id] = validIds;
      const p1 = playersById[p1Id];
      const p2 = playersById[p2Id];
      const p1Elo = p1.gender && p2.gender && p1.gender !== p2.gender
        ? (p1.mixed_elo_rating || 0)
        : (p1.levels_elo_rating || 0);
      const p2Elo = p1.gender && p2.gender && p1.gender !== p2.gender
        ? (p2.mixed_elo_rating || 0)
        : (p2.levels_elo_rating || 0);
      return Math.round((p1Elo + p2Elo) / 2);
    }
    if (validIds.length === 1) {
      const p = playersById[validIds[0]];
      return Math.round(Number(p?.levels_elo_rating) || 0);
    }
    return Math.round(
      validIds.reduce((sum, id) => {
        const player = playersById[id];
        return sum + (player?.levels_elo_rating || 0);
      }, 0) / (validIds.length || 1)
    );
  };
  
  const team1AvgElo = getTeamAvgElo(court.team1);
  const team2AvgElo = getTeamAvgElo(court.team2);
  
  // Get player names for display
  const getTeamPlayerNames = (teamIds) => {
    return teamIds.map(id => playersById[id]?.name || '').filter(Boolean).join(' & ');
  };
  
  const team1Names = getTeamPlayerNames(court.team1);
  const team2Names = getTeamPlayerNames(court.team2);
  
  const handleSubmitResult = async () => {
    if (isSubmittingScore) return; // Prevent double submission
    if (court.team1.length !== playersPerSide || court.team2.length !== playersPerSide) {
      return;
    }
    // Validate scores before submitting
    const validationError = validateBadmintonScore(team1Score, team2Score);
    if (validationError) {
      setScoreError(validationError);
      setSubmitError('');
      return;
    }

    setSubmitError('');
    
    // Convert scores to numbers
    const score1 = parseInt(team1Score) || 0;
    const score2 = parseInt(team2Score) || 0;
    
    // Determine winner based on scores
    const winningTeam = score1 > score2 ? 1 : 2; // Use 1/2, not 'team1'/'team2'
    
    // Look up player objects for both teams
    const team1Players = court.team1.map(id => playersById[id]);
    const team2Players = court.team2.map(id => playersById[id]);

    setIsSubmittingScore(true);
    scoreSubmitDebugLog('uiSubmit', {
      courtIndex,
      courtNumber: court.courtNumber ?? courtIndex + 1,
      team1Score: score1,
      team2Score: score2,
    });
    try {
      await onUpdateResult(courtIndex, {
        team1Score: score1,
        team2Score: score2,
        winningTeam,
        team1Players,
        team2Players
      }, testModeEnabled);
    } catch (err) {
      console.error('[CourtAssignment] Score submit failed:', err);
      const msg =
        err?.name === 'ScoreSubmissionClaimError' && err?.message
          ? err.message
          : 'Failed to save score. Please try again.';
      setSubmitError(msg);
      return;
    } finally {
      setIsSubmittingScore(false);
    }

    setTeam1Score('');
    setTeam2Score('');
    setScoreError('');
    setSubmitError('');
  };
  
  const handleRotatePlayers = () => {
    setIsRotating(true);
    try {
      onRotatePlayers();
    } catch (error) {
      console.error("Error rotating players:", error);
    } finally {
      setIsRotating(false);
    }
  };

  // Handle score input changes with 2-digit limit and auto-focus
  const handleScoreChange = (team, value) => {
    // Allow empty string or numbers, but limit to 2 digits max
    if (value === '') {
      if (team === 'team1') {
        setTeam1Score('');
      } else {
        setTeam2Score('');
      }
      return;
    }
    
    // Only allow digits and limit to 2 digits
    if (/^\d+$/.test(value) && value.length <= 2) {
      if (team === 'team1') {
        setTeam1Score(value);
        // Auto-focus to team2 input when 2 digits are entered
        if (value.length === 2 && team2ScoreInputRef.current) {
          markSkipMobileScoreClear('team2');
          team2ScoreInputRef.current.focus();
        }
      } else {
        setTeam2Score(value);
        // Auto-focus to team1 input when 2 digits are entered (if team1 doesn't have 2 digits yet)
        if (value.length === 2 && team1Score.length < 2 && team1ScoreInputRef.current) {
          markSkipMobileScoreClear('team1');
          team1ScoreInputRef.current.focus();
        }
      }
    }
  };
  
  // Handle increment/decrement for scores
  const handleScoreIncrement = (team, delta) => {
    const currentScore = team === 'team1' ? team1Score : team2Score;
    const currentValue = parseInt(currentScore) || 0;
    const newValue = Math.max(0, Math.min(99, currentValue + delta));
    
    if (team === 'team1') {
      setTeam1Score(newValue.toString());
    } else {
      setTeam2Score(newValue.toString());
    }
  };
  
  // Validate scores whenever they change
  useEffect(() => {
    if (team1Score !== '' && team2Score !== '') {
      const validationError = validateBadmintonScore(team1Score, team2Score);
      setScoreError(validationError);
      if (!validationError) setSubmitError('');
    } else {
      setScoreError('');
    }
  }, [team1Score, team2Score, scoringConfig]);
  
  // Add a utility for valid end state
  const canEndMatch =
    court.team1.length === court.team2.length && court.team1.length === playersPerSide;

  const rosterCompleteForSubmit =
    court.team1.length === playersPerSide && court.team2.length === playersPerSide;
  const submitDisabled =
    isSubmittingScore ||
    !!scoreError ||
    team1Score === '' ||
    team2Score === '' ||
    !rosterCompleteForSubmit;
  const submitHoverMessage = !rosterCompleteForSubmit
    ? `Both teams need ${playersPerSide} player${playersPerSide > 1 ? 's' : ''} before you can submit.`
    : scoreError
      ? scoreError
      : team1Score === '' || team2Score === ''
        ? 'Enter a valid score'
        : '';

  // Render a team panel - memoized to prevent excessive re-renders
  const renderTeamPanel = useCallback((playerIds, teamNumber, playersById) => {
    const avgElo = getTeamAvgElo(playerIds);
    const isWinner = court.winner === `team${teamNumber}`;
    return (
      <TeamPanel
        team={playerIds}
        teamNumber={teamNumber}
        playersById={playersById}
        avgElo={avgElo}
        isWinner={isWinner}
        canRemove={!!onRemovePlayerFromCourt}
        onRemovePlayer={onRemovePlayerFromCourt}
        onAddPlayer={handleAddPlayerToPosition}
        courtNumber={court.courtNumber}
        showEloChange={!!court.score && showEloTooltip}
        eloDeltas={court.eloDeltas}
        isSocialSession={isSocialSession}
        availablePlayers={availablePlayers}
        waitingManagementPlayers={waitingManagementPlayers}
        sessionPlayerStatus={sessionPlayerStatus}
        score={court.score}
        nextAssignments={nextAssignments}
        onRemoveFromUpcoming={onRemovePlayerFromCourt}
        playerStreaks={playerStreaks}
        emptySlotsReadOnly={!onAddPlayerToCourt || rosterLocked}
        maxPlayersPerTeam={playersPerSide}
        density={gridDensity}
        courtPlayerDragEnabled={!isSocialSession && !rosterLocked && !!onRemovePlayerFromCourt}
        playerWaitMetrics={playerWaitMetrics}
        showEloTooltip={showEloTooltip}
        pegBoardQueue={pegBoardQueue}
      />
    );
  }, [court.winner, court.courtNumber, court.score, court.eloDeltas, onRemovePlayerFromCourt, handleAddPlayerToPosition, isSocialSession, rosterLocked, availablePlayers, waitingManagementPlayers, sessionPlayerStatus, nextAssignments, onAddPlayerToCourt, playersPerSide, playersById, gridDensity, playerWaitMetrics, showEloTooltip, pegBoardQueue]);

  // Check if court is empty (has spots that can be filled)
  const isCourtEmpty =
    court.team1.length < playersPerSide || court.team2.length < playersPerSide;
  
  // Check if court is actually empty (no players at all)
  const isCourtActuallyEmpty = court.team1.length === 0 && court.team2.length === 0;
  
  // Check if there are upcoming matches
  const hasUpcomingMatches = nextAssignments && nextAssignments.length > 0;
  
  // Debug logging removed for cleaner console
  
  // REMOVED: handleFillEmptyCourtWaitlistOnly function
  // Simplified to use the main handleFillEmptyCourt function only

  // Determine court state for competitive buttons
  const isPlayingCourtEmpty = court.team1.length === 0 && court.team2.length === 0;
  const playingCourtPlayerCount = court.team1.length + court.team2.length;
  const playersPerMatch = playersPerSide * 2;
  const isPlayingCourtFull = playingCourtPlayerCount === playersPerMatch;
  const firstNext = nextAssignments.length > 0 ? nextAssignments[0] : null;
  const t1Count = firstNext ? (firstNext.team1?.length || 0) : 0;
  const t2Count = firstNext ? (firstNext.team2?.length || 0) : 0;
  const nextOnCourtCount = t1Count + t2Count;
  // Must match computePromotionPlan in CompetitiveSessionPage: both sides full, not merely N players total.
  const isNextOnCourtFull =
    Boolean(firstNext) && t1Count === playersPerSide && t2Count === playersPerSide;
  const isNextOnCourtPartial = nextOnCourtCount > 0 && !isNextOnCourtFull;
  const isNextOnCourtEmpty = nextOnCourtCount === 0;
  // Note: "Generate Game" buttons should ONLY appear when playing court is empty (0/4 players)
  // When court has active players (1-4), no generation buttons should show

  const hidePegBoardPickForPromote = Boolean(isNextOnCourtFull && onPromoteUpcomingMatches);
  const isPegBoardPickTargetCourt = isPegBoardPickTargetActive(pegBoardPickTarget, court.courtNumber);
  const showPegBoardPick =
    isPlayingCourtEmpty &&
    typeof onOpenPegBoardPick === 'function' &&
    !hidePegBoardPickForPromote &&
    isPegBoardPickTargetCourt;
  const pegBoardPicker =
    isPlayingCourtEmpty && !hidePegBoardPickForPromote && isPegBoardPickTargetCourt
      ? pegBoardPickerFromQueue(pegBoardQueue, playersById)
      : null;
  const pegBoardFillFirstLabel =
    isPlayingCourtEmpty &&
    !hidePegBoardPickForPromote &&
    pegBoardPickTarget &&
    !isPegBoardPickTargetCourt
      ? formatPegBoardPickTargetLabel(pegBoardPickTarget)
      : '';
  const isPegBoardEmptyCourt = Boolean(pegBoardQueue) && isPlayingCourtEmpty;
  const pegBoardPickIsViewerTurn =
    Boolean(viewerPlayerId && pegBoardPicker?.id && viewerPlayerId === pegBoardPicker.id);
  const pegBoardPickButton = showPegBoardPick ? (
    <Button
      type="button"
      onClick={onOpenPegBoardPick}
      variant="primary"
      size="small"
      className="w-full min-h-[44px] sm:min-h-0"
      title="Pick the next game from the queue"
    >
      {pegBoardPickIsViewerTurn ? 'Your turn to pick the game' : 'Pick game'}
    </Button>
  ) : null;

  const hasVisibleActions =
    (showPegBoardPick && !isPegBoardEmptyCourt) ||
    (isPlayingCourtEmpty && isNextOnCourtFull && !!onPromoteUpcomingMatches) ||
    (isPlayingCourtEmpty && (isNextOnCourtPartial || isNextOnCourtEmpty) && !!onFillEmptyCourt) ||
    (!!court.winner && showPostMatchRotateButton) ||
    !!court.unbalancedInfo;

  const scoreEntryActive =
    canEnterMatchScores &&
    !isCourtActuallyEmpty &&
    !court.winner &&
    (court.inProgress || rosterCompleteForSubmit || isSocialSession);

  /** Keep score rail mounted during submit so optimistic court rotation does not collapse the grid. */
  const showScoreRail = scoreEntryActive || isSubmittingScore;

  const scoreRailTeam1Input = (
    <div className="relative flex h-full min-h-0 w-full flex-col justify-center py-0.5" aria-busy={isSubmittingScore}>
      <input
        ref={team1ScoreInputRef}
        placeholder="0"
        value={team1Score}
        onChange={(e) => handleScoreChange('team1', e.target.value)}
        onFocus={(e) => handleMobileScoreFocus(e, 'team1', () => setTeam1Score(''))}
        onKeyDown={(e) => {
          if (
            e.key === 'Enter' &&
            rosterCompleteForSubmit &&
            !scoreError &&
            team1Score &&
            team2Score
          ) {
            handleSubmitResult();
          }
        }}
        maxLength={2}
        disabled={isSubmittingScore}
        autoComplete="off"
        className={`w-full bg-transparent px-0 text-center font-bold tabular-nums text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-0 sm:pr-2 ${
          densityCompact ? 'text-lg leading-tight sm:text-xl' : 'text-xl leading-tight sm:text-2xl'
        } disabled:opacity-50`}
        aria-label="Team 1 score"
        {...mobileScoreInputAttrs}
      />
      <div className="absolute right-0 top-1/2 hidden -translate-y-1/2 flex-col gap-px pr-px sm:flex">
        <button
          type="button"
          onClick={() => handleScoreIncrement('team1', 1)}
          disabled={isSubmittingScore}
          className="flex h-4 w-4 items-center justify-center rounded-sm text-[10px] leading-none text-text-secondary hover:bg-black/8 hover:text-text-primary dark:hover:bg-white/10"
          aria-label="Increase Team 1 score"
        >
          ▲
        </button>
        <button
          type="button"
          onClick={() => handleScoreIncrement('team1', -1)}
          disabled={isSubmittingScore}
          className="flex h-4 w-4 items-center justify-center rounded-sm text-[10px] leading-none text-text-secondary hover:bg-black/8 hover:text-text-primary dark:hover:bg-white/10"
          aria-label="Decrease Team 1 score"
        >
          ▼
        </button>
      </div>
    </div>
  );

  const scoreRailTeam2Input = (
    <div className="relative flex h-full min-h-0 w-full flex-col justify-center py-0.5">
      <input
        ref={team2ScoreInputRef}
        placeholder="0"
        value={team2Score}
        onChange={(e) => handleScoreChange('team2', e.target.value)}
        onFocus={(e) => handleMobileScoreFocus(e, 'team2', () => setTeam2Score(''))}
        onKeyDown={(e) => {
          if (
            e.key === 'Enter' &&
            rosterCompleteForSubmit &&
            !scoreError &&
            team1Score &&
            team2Score
          ) {
            handleSubmitResult();
          }
        }}
        maxLength={2}
        disabled={isSubmittingScore}
        autoComplete="off"
        className={`w-full bg-transparent px-0 text-center font-bold tabular-nums text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-0 sm:pr-2 ${
          densityCompact ? 'text-lg leading-tight sm:text-xl' : 'text-xl leading-tight sm:text-2xl'
        } disabled:opacity-50`}
        aria-label="Team 2 score"
        {...mobileScoreInputAttrs}
      />
      <div className="absolute right-0 top-1/2 hidden -translate-y-1/2 flex-col gap-px pr-px sm:flex">
        <button
          type="button"
          onClick={() => handleScoreIncrement('team2', 1)}
          disabled={isSubmittingScore}
          className="flex h-4 w-4 items-center justify-center rounded-sm text-[10px] leading-none text-text-secondary hover:bg-black/8 hover:text-text-primary dark:hover:bg-white/10"
          aria-label="Increase Team 2 score"
        >
          ▲
        </button>
        <button
          type="button"
          onClick={() => handleScoreIncrement('team2', -1)}
          disabled={isSubmittingScore}
          className="flex h-4 w-4 items-center justify-center rounded-sm text-[10px] leading-none text-text-secondary hover:bg-black/8 hover:text-text-primary dark:hover:bg-white/10"
          aria-label="Decrease Team 2 score"
        >
          ▼
        </button>
      </div>
    </div>
  );

  const scoreRailFooter = (
    <div
      className={`relative group w-full min-w-0 ${densityCompact ? 'pt-1' : 'pt-1.5'}`}
      aria-busy={isSubmittingScore}
    >
      <Button
        onClick={handleSubmitResult}
        variant={isSubmittingScore || submitDisabled ? 'default' : 'success'}
        disabled={submitDisabled}
        size="small"
        className={`h-8 w-full min-w-0 rounded-lg px-1.5 font-medium ${densityCompact ? 'text-xs' : 'text-xs sm:text-sm'}`}
      >
        {isSubmittingScore ? (
          <>
            <span
              className="mr-1.5 inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current/35 border-t-current"
              aria-hidden
            />
            Recording…
          </>
        ) : (
          'Submit'
        )}
      </Button>
      {submitDisabled && !isSubmittingScore && submitHoverMessage && (
        <div className="absolute top-full left-1/2 z-10 mt-2 max-w-[14rem] -translate-x-1/2 rounded-lg border border-border-medium bg-surface-elevated px-3 py-2 text-center text-xs text-text-primary opacity-0 shadow-brand-md transition-opacity duration-200 pointer-events-none group-hover:opacity-100">
          {submitHoverMessage}
        </div>
      )}
    </div>
  );

  const scoreRail = showScoreRail
    ? {
        team1: scoreRailTeam1Input,
        team2: scoreRailTeam2Input,
        footer: scoreRailFooter,
      }
    : null;

  const scoreBodyFooter = (() => {
    if (!showScoreRail) return null;
    return (
      <div className="space-y-1">
        <p className="text-center text-xs text-text-muted">
          {getScoringContextLabel(scoringConfig)}
        </p>
        {scoreError && (
          <p className="text-center text-xs text-danger">{scoreError}</p>
        )}
        {submitError && (
          <p className="text-center text-xs text-danger">{submitError}</p>
        )}
      </div>
    );
  })();

  const hasCourtPlayers = (court.team1?.length || 0) + (court.team2?.length || 0) > 0;
  const partnershipSplitFooter = hasCourtPlayers ? (
    <PartnershipSplitNotice
      team1={court.team1}
      team2={court.team2}
      partnerships={partnerships}
      playersById={playersById}
      className={scoreBodyFooter ? 'mb-2' : ''}
    />
  ) : null;

  const combinedBodyFooter =
    partnershipSplitFooter || scoreBodyFooter ? (
      <>
        {partnershipSplitFooter}
        {scoreBodyFooter}
      </>
    ) : null;
  
  // Actions for buttons and controls (omit wrapper entirely when empty so CourtPanel skips footer padding)
  const actions = hasVisibleActions ? (
    <div className="flex flex-col gap-1 w-full">
      {/* COMPETITIVE BUTTONS FOR EMPTY PLAYING COURT */}
      {isPlayingCourtEmpty && (
        <>
          {isPegBoardEmptyCourt ? null : pegBoardPickButton}
          {/* Case 1: Empty playing + Full next-on-court = Promote Upcoming Match */}
          {isNextOnCourtFull && onPromoteUpcomingMatches && (
            <Button
              onClick={() => {
                console.log(`🔄 [Court ${court.courtNumber}] Promoting upcoming match to playing court`);
                onPromoteUpcomingMatches();
              }}
              variant="primary"
              size="small"
              title="Move next-on-court players to this court"
            >
              Promote Upcoming Match
            </Button>
          )}
          
          {/* Case 2: Empty playing + Partial/Empty next-on-court = Create Game */}
          {(isNextOnCourtPartial || isNextOnCourtEmpty) && onFillEmptyCourt && (
            <div className="flex flex-col items-start gap-1">
              <Button
                onClick={() => {
                  console.log(`🔄 [Court ${court.courtNumber}] Starting game (${nextOnCourtCount} partial players will be returned to waitlist)`);
                  onFillEmptyCourt(court.courtNumber);
                }}
                variant={matchmakingValidation.possible ? "primary" : "secondary"}
                size="small"
                disabled={!matchmakingValidation.possible}
                title={
                  !matchmakingValidation.possible
                    ? matchmakingValidation.reason
                    : isNextOnCourtPartial
                      ? `Return ${nextOnCourtCount} next-on-court players to the queue and generate a fresh game`
                      : "Generate a new game from the queue"
                }
              >
                Create Game
              </Button>
              
              {!matchmakingValidation.possible && (
                <p className="text-xs text-warning-dark italic">
                  ⚠️ {matchmakingValidation.reason}
                </p>
              )}
            </div>
          )}
        </>
      )}
      
      {court.winner && showPostMatchRotateButton && (
        <div className="flex w-full flex-col items-stretch gap-2">
          <div className="flex w-full flex-wrap items-center justify-center py-0.5">
            <Button
              onClick={handleRotatePlayers}
              disabled={isRotating}
              variant="warning"
              size="small"
            >
              {isRotating ? 'Rotating Players...' : 'Rotate All Players'}
            </Button>
          </div>
        </div>
      )}
      
      {/* Unbalanced game tooltip */}
      {court.unbalancedInfo && (
        <div className="mt-2 flex items-center justify-center">
          <div className="relative group">
            <span className="text-sm text-gray-600 cursor-help">
              No ELO change
            </span>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              {court.unbalancedInfo.message}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const canRaiseCourtNumber =
    typeof onMoveCourt === 'function' &&
    Number(court.courtNumber) < Number(playingCourtCount);
  const canLowerCourtNumber = typeof onMoveCourt === 'function' && Number(court.courtNumber) > 1;
  const canMoveCourtUp = courtMoveFollowsStack ? canLowerCourtNumber : canRaiseCourtNumber;
  const canMoveCourtDown = courtMoveFollowsStack ? canRaiseCourtNumber : canLowerCourtNumber;
  const moveCourtUpBy = courtMoveFollowsStack ? -1 : 1;
  const moveCourtDownBy = courtMoveFollowsStack ? 1 : -1;
  const showCourtNumberStepper = canMoveCourtUp || canMoveCourtDown;
  const courtNumberMoveEnabled = !courtReorderBusy
    ? 'hover:bg-black/5 hover:text-text-primary dark:hover:bg-white/10'
    : 'cursor-not-allowed opacity-55';
  const moveUpLabel = courtMoveFollowsStack ? 'Move court up' : 'Move court number up';
  const moveDownLabel = courtMoveFollowsStack ? 'Move court down' : 'Move court number down';

  return (
    <CourtPanel
      title={
        <div className="flex items-center gap-2">
          <span>{`Court ${court.courtNumber}`}</span>
          {showCourtNumberStepper ? (
            <div className="inline-flex shrink-0 items-stretch overflow-hidden rounded-md border border-border-medium bg-surface-subtle">
              {canMoveCourtUp ? (
                <button
                  type="button"
                  onClick={() => onMoveCourt(court.courtNumber, moveCourtUpBy)}
                  disabled={courtReorderBusy}
                  aria-label={moveUpLabel}
                  title={moveUpLabel}
                  className={`${COURT_NUMBER_MOVE_BTN} border-0 bg-transparent ${canMoveCourtDown ? 'border-r border-border-medium' : ''} ${courtNumberMoveEnabled}`}
                >
                  <CourtNumberMoveChevron direction={-1} />
                </button>
              ) : null}
              {canMoveCourtDown ? (
                <button
                  type="button"
                  onClick={() => onMoveCourt(court.courtNumber, moveCourtDownBy)}
                  disabled={courtReorderBusy}
                  aria-label={moveDownLabel}
                  title={moveDownLabel}
                  className={`${COURT_NUMBER_MOVE_BTN} border-0 bg-transparent ${courtNumberMoveEnabled}`}
                >
                  <CourtNumberMoveChevron direction={1} />
                </button>
              ) : null}
            </div>
          ) : null}
          <CourtTimer startTime={court.startTime} isInProgress={court.inProgress} />
          <MatchmakingRelaxedBadge
            relaxed={court.matchmakingRelaxed}
            relaxationDetails={court.matchmakingRelaxationDetails}
            variant="court"
          />
          {rosterLocked && <ScheduledGameLockBadge />}
        </div>
      }
      headerRight={
        <div className="flex items-center gap-2">
          {court.matchmakingDebugInfo && (
            <MatchmakingDebugTooltip debugInfo={court.matchmakingDebugInfo} playersById={playersById} />
          )}
          {onRemoveCourt && (
          <button onClick={() => {
            console.log('🔄 CourtAssignment - Remove court clicked for courtIndex:', courtIndex, 'courtNumber:', court.courtNumber);
            onRemoveCourt(courtIndex);
          }} className="text-slate-500 hover:text-red-600 focus:outline-none" title="Remove Court">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          )}
        </div>
      }
      teams={[court.team1, court.team2]}
      playersById={playersById}
      renderTeamPanel={(team, idx, playersById) => renderTeamPanel(team, idx, playersById)}
      actions={actions}
      minHeight={
        isPegBoardEmptyCourt
          ? densityCompact
            ? 208
            : 300
          : courtCardLayout === 'mobilePlayer' ? 'auto' :
        canEnterMatchScores && !court.winner
          ? densityCompact
            ? 208
            : 300
          : 'auto'
      }
      density={gridDensity}
      highlightFreshRotation={highlightFreshRotation}
      highlightCallToAction={isPegBoardEmptyCourt && showPegBoardPick}
      scoreRail={scoreRail}
      bodyFooter={combinedBodyFooter}
      teamsLayout={courtCardLayout === 'mobilePlayer' ? 'sideBySide' : 'stack'}
      viewerPlayerId={viewerPlayerId}
      hideTeams={isPegBoardEmptyCourt}
      emptyBody={
        isPegBoardEmptyCourt ? (
          <PegBoardWaitingForGame
            compact={densityCompact}
            action={showPegBoardPick ? pegBoardPickButton : null}
          />
        ) : null
      }
      banner={
        isPegBoardEmptyCourt && pegBoardPickIsViewerTurn ? (
          <PegBoardYourTurnHint compact={densityCompact} />
        ) : pegBoardPicker ? (
            <PegBoardPickerHint player={pegBoardPicker} compact={densityCompact} />
          ) : pegBoardFillFirstLabel ? (
            <PegBoardFillFirstHint targetLabel={pegBoardFillFirstLabel} compact={densityCompact} />
          ) : null
      }
    />
  );
};

export default CourtAssignment; 